import { uuidv7 } from '@qsms/shared'
import { useEffect, useRef, useState } from 'react'
import { banco } from '../dados/banco.js'
import { useArquivo } from '../dados/useArquivo.js'

/// Assinatura desenhada a dedo. O Power Apps faz o mesmo; a diferença é que aqui o
/// traço vira um PNG guardado no aparelho e sobe junto com a inspeção.
///
/// Fundo transparente de propósito: a assinatura entra no PDF sobre a linha, e um
/// retângulo branco por cima da linha ficaria feio.

interface Props {
  inspecaoId: string
  arquivoId: string | null
  rotulo: string | null
  somenteLeitura?: boolean
  onMudar: (arquivoId: string | null, rotulo: string | null) => void
}

export function Assinatura({ inspecaoId, arquivoId, rotulo, somenteLeitura, onMudar }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const desenhando = useRef(false)
  const temTraco = useRef(false)
  const [desenhandoAgora, setDesenhandoAgora] = useState(false)

  // A assinatura já gravada: blob do aparelho, ou busca autenticada no servidor
  // quando a inspeção veio de outro dispositivo.
  const previa = useArquivo(arquivoId).url

  useEffect(() => {
    if (!desenhandoAgora) return
    const canvas = canvasRef.current
    if (!canvas) return

    // Sem ajustar pela densidade de tela, o traço sai borrado no celular.
    const escala = window.devicePixelRatio || 1
    const retangulo = canvas.getBoundingClientRect()
    canvas.width = retangulo.width * escala
    canvas.height = retangulo.height * escala

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(escala, escala)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#0f172a'
  }, [desenhandoAgora])

  function posicao(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - r.left, y: e.clientY - r.top }
  }

  function comecar(e: React.PointerEvent<HTMLCanvasElement>) {
    e.currentTarget.setPointerCapture(e.pointerId)
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = posicao(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    desenhando.current = true
  }

  function mover(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!desenhando.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = posicao(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    temTraco.current = true
  }

  function terminar() {
    desenhando.current = false
  }

  function limparCanvas() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    temTraco.current = false
  }

  async function aplicar() {
    const canvas = canvasRef.current
    if (!canvas || !temTraco.current) return

    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/png'))
    if (!blob) return

    const novoId = uuidv7()
    await banco.fotos.add({
      arquivoId: novoId,
      inspecaoId,
      blob,
      mimeType: 'image/png',
      criadoEm: new Date().toISOString(),
      enviado: false,
    })
    // A assinatura anterior sai do aparelho só depois que a nova estiver gravada na
    // resposta — quem grava cuida disso.
    onMudar(novoId, rotulo)
    setDesenhandoAgora(false)
    temTraco.current = false
  }

  function remover() {
    onMudar(null, null)
  }

  if (somenteLeitura) {
    return previa ? (
      <div>
        <img src={previa} alt="Assinatura" className="h-24 w-full rounded-lg border border-slate-200 bg-white object-contain" />
        {rotulo && <p className="mt-1 text-center text-xs text-slate-500">{rotulo}</p>}
      </div>
    ) : (
      <p className="text-sm text-slate-400">Sem assinatura.</p>
    )
  }

  if (desenhandoAgora) {
    return (
      <div>
        <canvas
          ref={canvasRef}
          // `touch-none` impede a página de rolar enquanto o dedo desenha.
          className="h-40 w-full touch-none rounded-lg border-2 border-dashed border-marinho bg-white"
          onPointerDown={comecar}
          onPointerMove={mover}
          onPointerUp={terminar}
          onPointerLeave={terminar}
        />
        <p className="mt-1 text-center text-xs text-slate-500">Assine com o dedo</p>
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={limparCanvas} className="botao-neutro flex-1">
            Limpar
          </button>
          <button type="button" onClick={() => setDesenhandoAgora(false)} className="botao-neutro flex-1">
            Cancelar
          </button>
          <button type="button" onClick={() => void aplicar()} className="botao-primario flex-1">
            Aplicar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      {previa ? (
        <>
          <img src={previa} alt="Assinatura" className="h-24 w-full rounded-lg border border-slate-200 bg-white object-contain" />
          {rotulo && <p className="mt-1 text-center text-xs text-slate-500">{rotulo}</p>}
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => setDesenhandoAgora(true)} className="botao-neutro flex-1">
              Assinar de novo
            </button>
            <button type="button" onClick={remover} className="botao-perigo flex-1">
              Remover
            </button>
          </div>
        </>
      ) : (
        <button type="button" onClick={() => setDesenhandoAgora(true)} className="botao-neutro w-full">
          Assinar
        </button>
      )}
    </div>
  )
}
